// Presentational progress dashboard — the advanced per-learner view shared by
// the parent route (/$locale/progress) and the teacher route
// (/$locale/teacher/student/:id). Pure: takes a KidProgress, renders the cards.
// docs/replan-v2.md R5 (parent) + R9 (teacher reuse).

import { useT } from '@fajrarabi/i18n/react';
import { Card, CardBody } from '@fajrarabi/ui';

import type { KidProgress } from '~/lib/learn';

export function ProgressDashboard({ progress: p }: { progress: KidProgress }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <TimeCard p={p} />
      <StreakCard p={p} />
      <XpCard p={p} />
      <LessonsCard p={p} />
      <div className="sm:col-span-2">
        <HeatmapCard p={p} />
      </div>
      <SubjectsCard p={p} />
      <TopActivitiesCard p={p} />
    </div>
  );
}

function DashCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card>
      <CardBody>
        <p className="text-xs font-bold uppercase tracking-wider text-ink-500">{title}</p>
        <div className="mt-3">{children}</div>
      </CardBody>
    </Card>
  );
}

function mins(ms: number): number {
  return Math.round(ms / 60000);
}

function TimeCard({ p }: { p: KidProgress }) {
  const t = useT();
  return (
    <DashCard title={t('app.progress.time_spent')}>
      <div className="flex items-end justify-between gap-2">
        <Metric
          label={t('app.progress.this_week')}
          value={t('app.progress.minutes', { n: mins(p.timeSpentMs.week) })}
        />
        <Metric
          label={t('app.progress.this_month')}
          value={t('app.progress.minutes', { n: mins(p.timeSpentMs.month) })}
        />
        <Metric
          label={t('app.progress.all_time')}
          value={t('app.progress.minutes', { n: mins(p.timeSpentMs.allTime) })}
        />
      </div>
    </DashCard>
  );
}

function StreakCard({ p }: { p: KidProgress }) {
  const t = useT();
  return (
    <DashCard title={t('app.progress.streak')}>
      <p className="text-2xl font-bold text-burgundy-700">
        🔥 {t('app.progress.streak_current', { n: p.streak.current })}
      </p>
      <p className="mt-1 text-sm text-ink-500">
        {t('app.progress.streak_longest', { n: p.streak.longest })}
      </p>
      {p.streak.freezeUsedThisWeek ? (
        <p className="mt-1 text-xs text-sky">{t('app.progress.freeze_used')}</p>
      ) : null}
    </DashCard>
  );
}

function XpCard({ p }: { p: KidProgress }) {
  const t = useT();
  const max = Math.max(1, ...p.xp.weekly);
  return (
    <DashCard title={t('app.progress.xp')}>
      <p className="text-2xl font-bold text-burgundy-700">
        ⭐ {t('app.progress.xp_total', { n: p.xp.total })}
      </p>
      <p className="mt-1 text-sm text-ink-500">{t('app.progress.xp_week', { n: p.xp.thisWeek })}</p>
      <div className="mt-3 flex h-12 items-end gap-1" aria-hidden="true">
        {p.xp.weekly.map((v, i) => (
          <span
            key={i}
            className="flex-1 rounded-t bg-sun-300"
            style={{ blockSize: `${Math.round((v / max) * 100)}%`, minBlockSize: '2px' }}
          />
        ))}
      </div>
    </DashCard>
  );
}

function LessonsCard({ p }: { p: KidProgress }) {
  const t = useT();
  return (
    <DashCard title={t('app.progress.lessons')}>
      <p className="text-2xl font-bold text-burgundy-700">
        {t('app.progress.lessons_completed', { n: p.lessons.completed })}
      </p>
      <p className="mt-1 text-sm text-ink-500">
        {t('app.progress.track_percent', { n: p.lessons.trackPercent })}
      </p>
      <div className="mt-3 h-3 overflow-hidden rounded-full bg-cream-200">
        <div
          className="h-full rounded-full bg-leaf"
          style={{ inlineSize: `${p.lessons.trackPercent}%` }}
        />
      </div>
    </DashCard>
  );
}

function HeatmapCard({ p }: { p: KidProgress }) {
  const t = useT();
  const max = Math.max(1, ...p.heatmap.map((d) => d.count));
  // 84 days → 12 columns of 7. Build week-major columns.
  const weeks: Array<typeof p.heatmap> = [];
  for (let i = 0; i < p.heatmap.length; i += 7) {
    weeks.push(p.heatmap.slice(i, i + 7));
  }
  return (
    <DashCard title={t('app.progress.activity_title')}>
      <div className="flex items-end gap-3">
        <div className="flex gap-1">
          {weeks.map((week, wi) => (
            <div key={wi} className="flex flex-col gap-1">
              {week.map((d) => (
                <span
                  key={d.date}
                  title={`${d.date}: ${d.count}`}
                  className="h-3 w-3 rounded-sm"
                  style={{
                    backgroundColor:
                      d.count === 0
                        ? 'var(--color-cream-200)'
                        : `color-mix(in srgb, var(--color-leaf) ${Math.round((d.count / max) * 100)}%, var(--color-cream-100))`,
                  }}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
      <div className="mt-3 flex gap-4 text-xs text-ink-500">
        <span>
          {t('app.progress.last_active')}:{' '}
          {p.lastActivityAt ? p.lastActivityAt.slice(0, 10) : t('app.progress.never')}
        </span>
        <span>
          {t('app.progress.last_login')}:{' '}
          {p.lastLoginAt ? p.lastLoginAt.slice(0, 10) : t('app.progress.never')}
        </span>
      </div>
    </DashCard>
  );
}

function SubjectsCard({ p }: { p: KidProgress }) {
  const t = useT();
  return (
    <DashCard title={t('app.progress.subjects_title')}>
      {p.subjects.length === 0 ? (
        <p className="text-sm text-ink-500">{t('app.progress.empty_activity')}</p>
      ) : (
        <ul className="space-y-2">
          {p.subjects.map((s) => (
            <li key={s.slug} className="flex items-center justify-between text-sm">
              <span className="font-medium text-ink-900">{s.slug}</span>
              <span className="font-bold text-burgundy-700">{s.completed}</span>
            </li>
          ))}
        </ul>
      )}
    </DashCard>
  );
}

function TopActivitiesCard({ p }: { p: KidProgress }) {
  const t = useT();
  return (
    <DashCard title={t('app.progress.top_activities_title')}>
      {p.topActivityTypes.length === 0 ? (
        <p className="text-sm text-ink-500">{t('app.progress.empty_activity')}</p>
      ) : (
        <ul className="space-y-2">
          {p.topActivityTypes.map((a) => (
            <li key={a.type} className="flex items-center justify-between text-sm">
              <span className="font-medium text-ink-900">{a.type.replace(/_/g, ' ')}</span>
              <span className="font-bold text-burgundy-700">{a.count}</span>
            </li>
          ))}
        </ul>
      )}
    </DashCard>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="text-center">
      <p className="text-lg font-bold text-burgundy-700">{value}</p>
      <p className="mt-0.5 text-xs text-ink-500">{label}</p>
    </div>
  );
}
